import { useCallback } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../../../dao/ElementaryProgressReportDao";
import { buildDiffStudentSavePayload, studentRatingsChanged } from "./buildDiffStudentSavePayload";

const studentDisplayName = (student) => {
    const name = [student?.firstName, student?.lastName].filter(Boolean).join(" ").trim();
    return name || "Unknown student";
};

export const formatDiffPartialSaveToast = (failedStudents, savedCount, totalCount) => {
    const names = failedStudents.map(studentDisplayName);
    const shown = names.slice(0, 3);
    const extra = names.length > 3 ? ` and ${names.length - 3} more` : "";
    const who = `${shown.join(", ")}${extra}`;
    const verb = names.length === 1 ? "was" : "were";
    return `Saved ${savedCount} of ${totalCount} students. ${who} ${verb} not saved. Please try again.`;
};

const saveOneStudent = async ({ student, snapshot, ratingsToSave, commentsToSave, raterGuid, token }) => {
    try {
        const data = buildDiffStudentSavePayload({
            snapshot,
            ratings: ratingsToSave,
            comments: commentsToSave,
            studentKey: student.studentKey,
            raterGuid
        });
        const response = await ElementaryProgressReportDao({
            action: "updateStudentRatings",
            gradingCriteriaId: snapshot.gradingCriteriaId,
            gradingProfileStudentId: snapshot.gradingProfileStudentId,
            data,
            token
        });
        if (!response?.data?.payload) {
            return { student, ok: false, reason: "empty-payload" };
        }
        return { student, ok: true };
    } catch (error) {
        console.error(`useDiffRatingSave: save failed for ${student.studentKey}`, error);
        return { student, ok: false, reason: "error", error };
    }
};

/**
 * Fan-out updateStudentRatings for dirty students only.
 * Each PUT is diffRatings only (ratings and comments). On-level contentRatings
 * are omitted so a leave-flush cannot overwrite a later Grade Level Template save.
 * One student's failure does not cancel the rest; succeeded students are
 * committed locally so retry only sends the ones that failed.
 */
const useDiffRatingSave = ({
    snapshots,
    ratings,
    comments,
    students,
    token,
    userDetails,
    setHasChanges,
    reload,
    commitSavedStudents
}) => {
    const saveRatings = useCallback(
        async (overrides = {}) => {
            const ratingsToSave = overrides.ratings ?? ratings;
            const commentsToSave = overrides.comments ?? comments ?? {};
            const dirty = (students || []).filter((s) => {
                const snapshot = snapshots[s.studentKey];
                return snapshot && studentRatingsChanged(snapshot, ratingsToSave, s.studentKey, commentsToSave);
            });

            if (!dirty.length) {
                toast.info("There are no ratings to save.", { autoClose: 2000 });
                setHasChanges?.(false);
                return { saved: [], failed: [] };
            }

            const raterGuid = userDetails?.uid ?? null;
            const results = await Promise.all(
                dirty.map((student) =>
                    saveOneStudent({
                        student,
                        snapshot: snapshots[student.studentKey],
                        ratingsToSave,
                        commentsToSave,
                        raterGuid,
                        token
                    })
                )
            );

            const saved = results.filter((r) => r.ok);
            const failed = results.filter((r) => !r.ok);

            if (!failed.length) {
                setHasChanges?.(false);
                toast.success("Ratings successfully saved.", { autoClose: 3000 });
                if (reload) reload();
                return { saved, failed };
            }

            if (saved.length) {
                commitSavedStudents?.(
                    saved.map((r) => r.student.studentKey),
                    ratingsToSave,
                    commentsToSave
                );
                toast.error(
                    formatDiffPartialSaveToast(
                        failed.map((r) => r.student),
                        saved.length,
                        dirty.length
                    )
                );
            } else {
                toast.error("Ratings were not saved. Please try again later.");
            }

            return { saved, failed };
        },
        [snapshots, ratings, comments, students, token, userDetails, setHasChanges, reload, commitSavedStudents]
    );

    return { saveRatings };
};

export default useDiffRatingSave;
